import { Event } from "../../../base/common/events.js";
import { IDisposable } from "../../../base/common/lifecycle.js";
import { CancellationToken } from "../../../base/common/cancellation.js";
import { createDecorator } from "../../instantiation/common/registry.js";

export const IPreviewService = createDecorator<IPreviewService>("previewService");

/**
 * Preview rendering for scene resources (materials, textures, geometries, …).
 *
 * THE one place that renders previews. Hard constraint: previews are rendered
 * on the HOST page's ORIGINAL renderer (GPU-resident resources exist only
 * there, and we never create additional WebGL/WebGPU contexts — the editor is
 * injected into pages it does not own). Engine providers implement that rule;
 * consumers only see URLs and canvases.
 *
 * Phase 1 is the static {@link IPreviewService.getPreview}; the interactive
 * {@link IPreviewSession} API is typed from day one (static = one session
 * frame + encode) so click-to-orbit previews, TSL graph previews and per-node
 * previews extend this service instead of growing new render paths.
 */

export type PreviewShape = "sphere" | "cube" | "plane" | "cylinder" | "shaderball";

/**
 * Geometry preview shading. "normal" = surface normals (default); "wireframe"
 * = edges only; "uv" = a UV checker/coordinate texture mapped by the geometry's
 * uv attribute (reveals layout + distortion on the surface); "uvLayout" = the
 * mesh's triangles UNWRAPPED into 2D UV space (the flat UV-editor view, drawn on
 * the CPU — no renderer needed); "solid" = a neutral grey lit surface with a
 * wireframe overlay (form + topology at once); "vertexColor" = the geometry's
 * vertex colors, unlit. "normalVectors" = a face-orientation base (backfaces
 * red) plus little lines showing each vertex normal's DIRECTION (Blender-style
 * — the color-coded "normal" mode can't show which way a normal points). "tbn"
 * = the same base with the full tangent frame per vertex: tangent (red),
 * binormal/bitangent (green), normal (blue); tangents come from the `tangent`
 * attribute or are derived transiently from uv+normal+index. A mode is only
 * meaningful when the geometry has the attribute it needs ("uv"/"uvLayout"
 * need uv, "vertexColor" needs color, "normalVectors" needs normal, "tbn"
 * needs normal + (tangent or uv), …).
 */
export type PreviewDisplayMode = "normal" | "wireframe" | "uv" | "uvLayout" | "solid" | "vertexColor" | "normalVectors" | "tbn";

/**
 * Which per-vertex frame axes the "tbn" display mode draws as direction lines:
 * "normal" (blue), "tangent" (red), "bitangent" (green). The consumer toggles
 * these independently — showing only the normal is the old "normalVectors"
 * look, all three is the full tangent frame. Default (unset) = all three.
 * "tangent"/"bitangent" need the geometry's tangent attribute or uv (to derive
 * one); "normal" only needs the normal attribute.
 */
export type PreviewVectorAxis = "normal" | "tangent" | "bitangent";

/**
 * Texture preview channel view: "rgb" (default) shows the texture as-is; a
 * single channel renders as GRAYSCALE (r/g/b/a spread to rgb, opaque). Only
 * available when the texture's pixels are CPU-readable — see
 * `canIsolateTextureChannels` (compressed textures/render targets are not).
 */
export type PreviewTextureChannel = "rgb" | "r" | "g" | "b" | "a";

export interface PreviewOptions {
    /** Output pixel size; defaults to 128×128 (thumbnails). */
    width?: number;
    height?: number;
    /** Geometry/material previews only; default "sphere" for materials. */
    shape?: PreviewShape;
    /** Geometry previews only; default "normal" (falls back to wireframe if no normals). */
    displayMode?: PreviewDisplayMode;
    /**
     * Which frame axes the "tbn" display mode draws (default all three). Ignored
     * by other modes and when the geometry lacks the needed attribute.
     */
    vectorAxes?: readonly PreviewVectorAxis[];
    /**
     * Which UV set the "uv"/"uvLayout" modes read: 0 = the primary `uv`
     * (TEXCOORD_0), 1 = `uv1` (TEXCOORD_1), etc. Default 0. Ignored by other
     * modes and when the geometry lacks that set.
     */
    uvSet?: number;
    /**
     * Geometry previews with morph targets: the index of the blendshape to show
     * at full influence. Omitted or < 0 = the base shape (no morph). Ignored
     * when the geometry has no morphAttributes.
     */
    morphTarget?: number;
    /**
     * "auto" (default): reuse the owning scene's environment when resolvable,
     * else a fixed neutral studio rig. "neutral"/"host" force one side.
     */
    lighting?: "auto" | "neutral" | "host";
    /**
     * The target is a FRESH, caller-owned load (asset thumbnails) that was
     * never GPU-resident anywhere — when no owning renderer exists, the
     * provider may render it on the editor's current renderer (like whole-
     * object previews). Without this, unowned materials/geometries are
     * rejected (the owning-renderer invariant protects SCENE resources whose
     * GPU state lives on another renderer).
     */
    transient?: boolean;
    token?: CancellationToken;
}

export interface PreviewResult {
    /** Image URL (usually blob:). Owned by the service cache — do NOT revoke. */
    url: string;
    width: number;
    height: number;
}

export type PreviewFrameStage = "queueWait" | "renderSubmission" | "readback" | "pixelConversion" | "canvasCopy";
export type PreviewFrameDiagnostics = { readonly frames: number; readonly colorConversion?: "gpu" | "cpu"; readonly samples?: number } & Record<PreviewFrameStage, { meanMs: number; maxMs: number }>;

export interface IPreviewCapturedFrame extends IDisposable {
    readonly element: HTMLCanvasElement;
}

/** Reusable output, optionally providing independent frames for pipelined capture. */
export interface IPreviewFrameSession extends IDisposable {
    /** Maximum number of leased frames; each must be disposed before its slot is reused. */
    readonly capacity?: number;
    acquireFrame?(target: object): Promise<IPreviewCapturedFrame>;
    readonly usesHostRendering?: boolean;
    /** Completed frames, including preparation; times are wall-clock milliseconds, not GPU timer queries. */
    diagnostics?(): PreviewFrameDiagnostics;
    readonly element: HTMLCanvasElement;
    /** Resolve the target at use time. Calls must be awaited; failures reject. */
    render(target: object): Promise<void>;
}
export interface PreviewFrameSessionOptions {
    readonly rendering?: "scene" | "composited";
    /** Downscale the completed host canvas without changing its aspect ratio. */
    readonly scale?: number;
    readonly document: Document;
    /** Output changes the capture camera's aspect; camera fits the original view inside the canvas. */
    readonly framing?: "camera" | "output";
    /** Omit to fix the session to the camera's starting aspect, rounded to an even pixel width. */
    readonly width?: number;
    readonly height: number;
}

export interface PreviewSessionOptions extends PreviewOptions {
    devicePixelRatio?: number;
}

/**
 * Result of picking into an interactive preview at a canvas position
 * (Alt-hover inspection). Plain data — no three.js types leak out.
 */
export type PreviewPickResult =
    | {
        readonly type: "mesh";
        /** hit point in the geometry's local space */
        readonly position: { x: number; y: number; z: number };
        /** interpolated uv at the hit, when the geometry has uvs */
        readonly uv: { u: number; v: number } | null;
        readonly faceIndex: number;
        /** nearest vertex of the hit triangle */
        readonly vertexIndex: number;
        readonly vertexPosition: { x: number; y: number; z: number };
        /** the nearest vertex's color (0-1 floats), when the geometry has a color attribute */
        readonly vertexColor: { r: number; g: number; b: number; a: number } | null;
    }
    | {
        readonly type: "texture";
        readonly u: number;
        readonly v: number;
        /** pixel coordinate in the source image, top-left origin */
        readonly pixelX: number;
        readonly pixelY: number;
        /** displayed color at the cursor (RGBA 0-255, post-encode) */
        readonly color: { r: number; g: number; b: number; a: number };
        /**
         * The SOURCE value at the cursor, in unclamped linear floats, for HDR
         * (float/half-float) textures — the authored 4.08, not the 255 it
         * displays as. Independent of the exposure setting. Null for ordinary
         * 8-bit textures, where `color` already tells the whole story.
         */
        readonly sourceColor: { r: number; g: number; b: number; a: number } | null;
    }
    | {
        readonly type: "unavailable";
        /** "too-large": mesh above the raycast triangle budget */
        readonly reason: "too-large" | "unsupported";
    };

/**
 * Interactive preview (phase 2). The session owns a plain 2D/bitmaprenderer
 * canvas — NEVER an additional WebGL/WebGPU context. Frames are rendered on
 * demand on the owning renderer, serialized on the engine's shared GPU work
 * queue, and blitted into {@link element}. The consumer drives frames
 * (auto-rotate, pointer orbit) via {@link invalidate}.
 */
export interface IPreviewSession extends IDisposable {
    readonly element: HTMLCanvasElement;
    readonly onDidRenderFrame: Event<void>;
    /** Coalesced async frame request; at most one frame in flight per session. */
    invalidate(): void;
    setSize(width: number, height: number): void;
    setShape(shape: PreviewShape): void;
    /** Geometry shading mode (normal/wireframe/uv). No-op for other kinds. */
    setDisplayMode(mode: PreviewDisplayMode): void;
    /** Frame axes drawn by the "tbn" mode (normal/tangent/bitangent). No-op otherwise. */
    setVectorAxes(axes: readonly PreviewVectorAxis[]): void;
    /** UV set for the uv/uvLayout modes (0 = primary). No-op for other kinds/modes. */
    setUvSet(uvSet: number): void;
    /** Show a morph target (blendshape) at full influence; < 0 = base shape. No-op for non-morph geometry. */
    setMorphTarget(index: number): void;
    /** Texture channel view (grayscale isolation). No-op for other kinds. */
    setTextureChannel(channel: PreviewTextureChannel): void;
    /**
     * Show a specific MIP LEVEL (0 = full size). Renders the level by copying it
     * out with `copyTextureToTexture(..., srcLevel)` — no custom shader, which a
     * WebGPU host renderer could not compile (same constraint that shaped
     * {@link setTextureChannel}).
     *
     * Requires that call to support levels on the HOST renderer — see
     * {@link IPreviewSession.textureMipSupport}. Where it does not, the level is
     * ignored rather than silently showing mip 0 under a different label.
     * No-op for other kinds.
     */
    setTextureMipLevel(level: number): void;
    /**
     * Whether this session can actually show mip levels, and how many the texture
     * has. `supported: false` means the host renderer is too old for level-aware
     * `copyTextureToTexture` — UIs should disable the control and say why rather
     * than offer a choice that does nothing.
     */
    readonly textureMipSupport: { readonly supported: boolean; readonly levels: number; readonly reason?: string };
    /**
     * Texture exposure in STOPS (gain 2^stops, 0 = as authored; color only,
     * alpha untouched) — makes dim HDR buffers (bloom mips hold values ≪ 1)
     * readable. Applied on the CPU pixel copy, so >1 highlights already
     * clamped by the 8-bit path can't be recovered — brightening is the use
     * case. No-op for other kinds.
     */
    setTextureExposure(stops: number): void;
    /**
     * Picks into the preview at element-local CSS coordinates (Alt-hover
     * inspection): geometry sessions CPU-raycast the mesh (skipped above a
     * triangle budget → "too-large"), texture sessions map the cursor to
     * uv/pixel and sample the displayed color. Null = nothing hit there.
     * Synchronous and cheap — throttle to pointer events.
     */
    pick(x: number, y: number): PreviewPickResult | null;
    /**
     * LIVE mode: re-render on a timer instead of only on {@link invalidate}, so a
     * texture the page keeps writing into (a render target above all) stops
     * showing the frame it was selected on. `fps = 0` turns it off.
     *
     * The rate is capped and ticks are DROPPED whenever a frame is still in
     * flight — every tick is a render plus a readback, and on classic WebGL that
     * readback is synchronous on the host page's renderer. See
     * `plans/live-texture-inspection.md`.
     */
    setLive(fps: number): void;
    /**
     * Whether the preview is on screen. Kept separate from {@link setLive} so
     * pausing an off-screen preview does not lose the user's chosen rate. The
     * CONSUMER owns this: only the UI knows whether its panel is visible, and
     * the session's canvas can move between documents (popout).
     */
    setLiveVisible(visible: boolean): void;
    /** Orbit the preview camera (radians). No-op for flat targets (textures). */
    orbit(deltaX: number, deltaY: number): void;
    /**
     * Pan the view. Deltas are pointer movement as fractions of the view
     * height (pixels / element height) — the content follows the pointer and
     * hand speed is zoom-invariant.
     */
    pan(deltaX: number, deltaY: number): void;
    zoom(delta: number): void;
}

export interface IPreviewProvider {
    /**
     * Cheap and side-effect free: the provider understands the target AND can
     * resolve an owning renderer for it right now.
     */
    canPreview(target: object): boolean;
    /** Null = not previewable (no owning renderer, unsupported type). */
    renderPreview(target: object, options: PreviewOptions): Promise<PreviewResult | null>;
    /** Phase 2 — optional until the interactive slice lands. */
    createFrameSession?(target: object, options: PreviewFrameSessionOptions): IPreviewFrameSession | null;
    createSession?(target: object, options: PreviewSessionOptions): IPreviewSession | null;
    /** Drop cached preview(s) so the next request re-renders. */
    invalidate(target?: object): void;
}

export interface IPreviewService {
    readonly _serviceBrand: undefined;
    readonly onDidChangeProviders: Event<void>;
    registerProvider(provider: IPreviewProvider): IDisposable;
    canPreview(target: object): boolean;
    /**
     * Static preview image. Null = not previewable — consumers keep their
     * fallback (e.g. a kind icon). Targets are used transiently and never
     * retained (weak-registry constraint).
     */
    getPreview(target: object, options?: PreviewOptions): Promise<PreviewResult | null>;
    /** Interactive preview (phase 2). Null = not previewable / not implemented. */
    createFrameSession(target: object, options: PreviewFrameSessionOptions): IPreviewFrameSession | null;
    createSession(target: object, options?: PreviewSessionOptions): IPreviewSession | null;
    /**
     * Drop cached previews for a target (or all). For edits that don't bump a
     * resource's version (uniform tweaks, in-place attribute edits).
     */
    invalidate(target?: object): void;
}
